import MultiSelectInput from '@repo/ui/multiselect-input'
import RadioInput from '@repo/ui/radio-input'
import Typography from '@repo/ui/typography'

import { CurrencyEnum, currencyEnumOptions } from '../../lib/types/enums/common/currency.enum'
import { OLTypeEnum, olTypeEnumMapping } from '../../lib/types/enums/ol/ol-type.enum'
import {
  StaffPositionEnum,
  staffPositionEnumMapping
} from '../../lib/types/enums/staff/staff-position.enum'
import { RoleEnum } from '../../lib/types/enums/users/role.enum'
import { UserStatusEnum } from '../../lib/types/enums/users/user-status.enum'
import { UserFilters } from '../../lib/types/models/users/user'
import { t } from '../../locale/utils'

type Props = {
  filter?: UserFilters
  onFilterChange: (filter: UserFilters) => void
}

const roleOptions = Object.values(RoleEnum).map((role) => ({
  label: role,
  value: role
}))

const statusOptions = Object.values(UserStatusEnum).map((status) => ({
  label: status,
  value: status
}))

const staffPositionOptions = Object.values(StaffPositionEnum).map((position) => ({
  label: t(staffPositionEnumMapping[position]),
  value: position
}))

const olTypeOptions = Object.values(OLTypeEnum).map((type) => ({
  label: olTypeEnumMapping[type].label,
  value: type
}))

const SystemAdminUserFilter = ({ filter, onFilterChange }: Props) => {
  const isOlRoleSelected = filter?.roles?.length === 1 && filter.roles[0] === RoleEnum.Ol

  function setFilter<K extends keyof UserFilters>(key: K, value: UserFilters[K]) {
    onFilterChange({ ...filter, [key]: value })
  }

  return (
    <div className="flex flex-col gap-4 pt-2">
      <MultiSelectInput<RoleEnum>
        label="Roles"
        variant="primary"
        options={roleOptions}
        selectedOptions={filter?.roles?.map((role) => ({ label: role, value: role }))}
        onChange={(roles) => {
          const isOnlyOl = roles.length === 1 && roles[0] === RoleEnum.Ol

          onFilterChange({
            ...filter,
            roles,
            ...(isOnlyOl ? {} : { olTypes: undefined, currencies: undefined })
          })
        }}
      />

      <MultiSelectInput<StaffPositionEnum>
        label="Position"
        variant="primary"
        options={staffPositionOptions}
        selectedOptions={filter?.staffPositions?.map((position) => ({
          label: t(staffPositionEnumMapping[position]),
          value: position
        }))}
        onChange={(staffPositions) => setFilter('staffPositions', staffPositions)}
      />

      <MultiSelectInput<UserStatusEnum>
        label="Statuses"
        variant="primary"
        options={statusOptions}
        selectedOptions={filter?.statuses?.map((status) => ({ label: status, value: status }))}
        onChange={(statuses) => setFilter('statuses', statuses)}
      />

      <MultiSelectInput<OLTypeEnum>
        label="Type"
        variant="primary"
        disabled={!isOlRoleSelected}
        options={olTypeOptions}
        selectedOptions={
          isOlRoleSelected
            ? filter?.olTypes?.map((type) => ({
                label: olTypeEnumMapping[type].label,
                value: type
              }))
            : undefined
        }
        onChange={(olTypes) => setFilter('olTypes', olTypes)}
      />

      <MultiSelectInput<CurrencyEnum>
        label="Currency"
        variant="primary"
        disabled={!isOlRoleSelected}
        options={currencyEnumOptions}
        selectedOptions={
          isOlRoleSelected
            ? filter?.currencies?.map((currency) => ({
                label:
                  currencyEnumOptions.find((option) => option.value === currency)?.label ??
                  currency,
                value: currency
              }))
            : undefined
        }
        onChange={(currencies) => setFilter('currencies', currencies)}
      />

      <div className={'flex flex-col gap-2'}>
        <Typography variant={'label-semibold'}>Master LEC</Typography>

        <div className="flex flex-row gap-5">
          <RadioInput
            label={'All'}
            checked={filter?.isMasterLec === undefined}
            onCheck={() => setFilter('isMasterLec', undefined)}
          />
          <RadioInput
            label={'Master LEC'}
            checked={filter?.isMasterLec === true}
            onCheck={() => setFilter('isMasterLec', true)}
          />
          <RadioInput
            label={'Not Master LEC'}
            checked={filter?.isMasterLec === false}
            onCheck={() => setFilter('isMasterLec', false)}
          />
        </div>
      </div>

      <div className={'flex flex-col gap-2'}>
        <Typography variant={'label-semibold'}>User group</Typography>

        <div className="flex flex-row gap-5">
          <RadioInput
            label={'All'}
            checked={filter?.hasUserGroup === undefined}
            onCheck={() => setFilter('hasUserGroup', undefined)}
          />
          <RadioInput
            label={'With user group'}
            checked={filter?.hasUserGroup === true}
            onCheck={() => setFilter('hasUserGroup', true)}
          />
          <RadioInput
            label={'Without user group'}
            checked={filter?.hasUserGroup === false}
            onCheck={() => setFilter('hasUserGroup', false)}
          />
        </div>
      </div>
    </div>
  )
}

export default SystemAdminUserFilter
